import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import GridLayout, { WidthProvider } from 'react-grid-layout/legacy'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'
import { ink, surface, line, radius, shadow, font, transition } from '../tokens'
import { widgetVisual } from 'features/dashboard/widgetVisuals'
import SearchableSelect from 'components/SearchableSelect'

const FILTER_LABELS = {
  scope: 'Scope',
  domain: 'Domain',
  assignee: 'Assignee',
  date_range: 'Time range',
}

const DATE_RANGE_OPTIONS = [
  { value: '', label: 'Default range' },
  { value: '30', label: 'Next 30 days' },
  { value: '60', label: 'Next 60 days' },
  { value: '90', label: 'Next 90 days' },
]

const Grid = WidthProvider(GridLayout)

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`

const StackItem = styled.div`
  width: 100%;
`

const FlowGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  column-gap: 16px;
  grid-auto-rows: 4px;
  grid-auto-flow: row dense;
  align-items: start;
  @media (max-width: 1100px) {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }
`

const FlowItem = styled.div`
  min-width: 0;
`

const MASONRY_ROW = 4
const MASONRY_GAP = 16
const RGL_ROW_PX = 30
const RGL_MARGIN_PX = 16

const estimateRows = hHint => {
  if (!hHint || hHint < 1) return 24
  const px = hHint * RGL_ROW_PX + (hHint - 1) * RGL_MARGIN_PX
  return Math.ceil((px + MASONRY_GAP) / MASONRY_ROW)
}

const MasonryItem = ({ span, hHint, children }) => {
  const ref = useRef(null)
  const [rows, setRows] = useState(() => estimateRows(hHint))
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return undefined
    const measure = () => {
      const h = el.getBoundingClientRect().height
      if (h > 0) setRows(Math.ceil((h + MASONRY_GAP) / MASONRY_ROW))
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  return (
    <FlowItem style={{ gridColumn: `span ${span}`, gridRowEnd: `span ${rows}` }}>
      <div ref={ref}>{children}</div>
    </FlowItem>
  )
}

const Cell = styled.div`
  height: ${({ $fixed }) => ($fixed ? '100%' : 'auto')};
  display: flex;
  flex-direction: column;
  background: ${surface.card};
  border: 1px solid ${line.base};
  border-radius: ${radius.md};
  box-shadow: ${shadow.card};
  overflow: ${({ $fixed }) => ($fixed ? 'hidden' : 'visible')};
  transition: ${transition};

  &:hover {
    border-color: ${line.strong};
  }
`

const CellHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-shrink: 0;
  height: 40px;
  padding: 0 14px;
  border-bottom: 1px solid ${line.base};
  background: ${surface.card};
`

const HeaderTitle = styled.span`
  min-width: 0;
  font-size: ${font.body};
  font-weight: 700;
  letter-spacing: 0.01em;
  color: ${ink.base};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;
`

const IconTile = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  flex: 0 0 auto;
  border-radius: ${radius.sm};
  background: ${({ $bg }) => $bg};
  color: ${({ $fg }) => $fg};
  svg { font-size: 17px; }
`

const CellBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: ${({ $fixed }) => ($fixed ? 'auto' : 'visible')};
  overflow-x: auto;
  padding: 12px 14px;
  scrollbar-width: thin;
  scrollbar-color: ${line.strong} transparent;
  &::-webkit-scrollbar { width: 7px; height: 7px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: ${line.strong};
    border-radius: 999px;
  }
  &::-webkit-scrollbar-thumb:hover { background: ${ink.mute}; }
  &::-webkit-scrollbar-corner { background: transparent; }
`

const Placeholder = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  text-align: center;
  font-size: ${font.body};
  color: ${ink.mute};
`

const DragHandle = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
  cursor: grab;
  color: ${ink.mute};
  font-size: ${font.small};
  user-select: none;

  &:active {
    cursor: grabbing;
  }
`

const Grip = styled.span`
  font-size: ${font.emphasis};
  line-height: 1;
`

const BarControls = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: 1px solid ${({ $active }) => ($active ? line.strong : line.base)};
  border-radius: ${radius.xs};
  background: ${({ $active }) => ($active ? surface.wash : surface.card)};
  color: ${({ $active }) => ($active ? ink.base : ink.soft)};
  font-size: ${font.small};
  line-height: 1;
  cursor: pointer;
  transition: ${transition};

  &:hover {
    border-color: ${line.strong};
    color: ${ink.base};
  }
`

const ConfigWrap = styled.div`
  position: relative;
`

const ConfigPanel = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 30;
  width: 240px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  background: ${surface.card};
  border: 1px solid ${line.strong};
  border-radius: ${radius.sm};
  box-shadow: ${shadow.lift};
`

const ConfigTitle = styled.div`
  font-size: ${font.caption};
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${ink.mute};
`

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const FieldLabel = styled.span`
  font-size: ${font.small};
  font-weight: 600;
  color: ${ink.soft};
`

const ComingSoon = styled.span`
  font-size: ${font.small};
  color: ${ink.mute};
  font-style: italic;
`

const FilterControl = ({ filter, value, options, onChange }) => {
  const label = FILTER_LABELS[filter] || filter

  if (filter === 'date_range') {
    return (
      <Field>
        <FieldLabel>{label}</FieldLabel>
        <SearchableSelect
          value={value || null}
          onChange={val => onChange(val || undefined)}
          options={DATE_RANGE_OPTIONS.filter(opt => opt.value).map(opt => ({ value: opt.value, label: opt.label }))}
          clearable
          clearLabel='Default range'
          placeholder='Default range'
          ariaLabel={label}
        />
      </Field>
    )
  }

  const list = options || []
  const emptyLabel = filter === 'scope' ? 'Whole portfolio'
    : filter === 'domain' ? 'All domains'
    : filter === 'assignee' ? 'Everyone'
    : 'All'

  if (list.length === 0) {
    return (
      <Field>
        <FieldLabel>{label}</FieldLabel>
        <SearchableSelect
          value={null}
          options={[]}
          placeholder='Coming soon'
          disabled
          ariaLabel={label}
        />
        <ComingSoon>No options available yet.</ComingSoon>
      </Field>
    )
  }

  return (
    <Field>
      <FieldLabel>{label}</FieldLabel>
      <SearchableSelect
        value={value || null}
        onChange={val => onChange(val || undefined)}
        options={list.map(opt => ({ value: opt.value, label: opt.label }))}
        clearable
        clearLabel={emptyLabel}
        placeholder={emptyLabel}
        ariaLabel={label}
      />
    </Field>
  )
}

const WidgetConfig = ({ title, filters, settings, options, onChange }) => {
  const [open, setOpen] = useState(false)

  const handleChange = useCallback(
    (filter, value) => {
      onChange(prev => {
        const next = { ...(prev || {}) }
        if (value === undefined) delete next[filter]
        else next[filter] = value
        return next
      })
    },
    [onChange]
  )

  if (!filters || filters.length === 0) return null

  return (
    <ConfigWrap>
      <IconButton
        type="button"
        $active={open}
        aria-label={`Configure ${title}`}
        title="Configure widget"
        aria-expanded={open}
        onClick={() => setOpen(prev => !prev)}
      >
        ⚙
      </IconButton>
      {open && (
        <ConfigPanel onMouseDown={e => e.stopPropagation()}>
          <ConfigTitle>Configure {title}</ConfigTitle>
          {filters.map(filter => (
            <FilterControl
              key={filter}
              filter={filter}
              value={(settings || {})[filter]}
              options={options ? options[filter] : undefined}
              onChange={value => handleChange(filter, value)}
            />
          ))}
        </ConfigPanel>
      )}
    </ConfigWrap>
  )
}

const DashboardCanvas = ({ layout, widgets, editing, nodes, filterOptions, onLayoutChange, onRemove, isNarrow }) => {
  const catalogue = useMemo(() => {
    const map = {}
    ;(widgets || []).forEach(widget => {
      map[widget.key] = widget
    })
    return map
  }, [widgets])

  const settingsByKey = useMemo(() => {
    const map = {}
    ;(layout || []).forEach(item => {
      map[item.widgetKey] = item.settings
    })
    return map
  }, [layout])

  const handleSettingsChange = useCallback(
    (widgetKey, updater) => {
      if (!onLayoutChange) return
      onLayoutChange(prev =>
        (prev || []).map(item =>
          item.widgetKey === widgetKey
            ? { ...item, settings: updater(item.settings) }
            : item
        )
      )
    },
    [onLayoutChange]
  )

  const rglItems = useMemo(
    () =>
      (layout || []).map(item => {
        const meta = catalogue[item.widgetKey] || {}
        return {
          i: item.widgetKey,
          x: item.x,
          y: item.y,
          w: item.w,
          h: item.h,
          minW: meta.minW,
          minH: meta.minH,
        }
      }),
    [layout, catalogue]
  )

  const handleLayoutChange = items => {
    if (!onLayoutChange) return
    const mapped = items.map(item => ({
      widgetKey: item.i,
      x: item.x,
      y: item.y,
      w: item.w,
      h: item.h,
      settings: settingsByKey[item.i],
    }))
    onLayoutChange(mapped)
  }

  const renderCellInner = item => {
    const meta = catalogue[item.i] || {}
    const visual = widgetVisual(item.i)
    const Ico = visual.Icon
    const node = typeof nodes === 'function'
      ? nodes(item.i, settingsByKey[item.i] || {})
      : nodes
        ? nodes[item.i]
        : undefined
    return (
      <Cell $fixed={editing}>
        <CellHeader title={meta.description || undefined}>
          {editing && !isNarrow ? (
            <DragHandle className="widget-drag-handle">
              <Grip aria-hidden="true">⠿</Grip>
              <IconTile $bg={visual.bg} $fg={visual.fg}><Ico fontSize="inherit" /></IconTile>
              <HeaderTitle>{meta.title || item.i}</HeaderTitle>
            </DragHandle>
          ) : (
            <TitleGroup>
              <IconTile $bg={visual.bg} $fg={visual.fg}><Ico fontSize="inherit" /></IconTile>
              <HeaderTitle>{meta.title || item.i}</HeaderTitle>
            </TitleGroup>
          )}
          {editing && (
            <BarControls>
              <WidgetConfig
                title={meta.title || item.i}
                filters={meta.filters}
                settings={settingsByKey[item.i]}
                options={filterOptions}
                onChange={updater => handleSettingsChange(item.i, updater)}
              />
              <IconButton
                type="button"
                aria-label={`Remove ${meta.title || item.i}`}
                title="Remove widget"
                onClick={() => onRemove && onRemove(item.i)}
              >
                ✕
              </IconButton>
            </BarControls>
          )}
        </CellHeader>
        {node !== undefined && node !== null ? (
          <CellBody $fixed={editing}>
            {node}
          </CellBody>
        ) : (
          <Placeholder>Widget unavailable</Placeholder>
        )}
      </Cell>
    )
  }

  if (isNarrow) {
    return (
      <Stack>
        {rglItems.map(item => (
          <StackItem key={item.i}>{renderCellInner(item)}</StackItem>
        ))}
      </Stack>
    )
  }

  if (!editing) {
    return (
      <FlowGrid>
        {[...rglItems]
          .sort((a, b) => (a.y - b.y) || (a.x - b.x))
          .map(item => (
            <MasonryItem key={item.i} span={Math.min(item.w, 12)} hHint={item.h}>
              {renderCellInner(item)}
            </MasonryItem>
          ))}
      </FlowGrid>
    )
  }

  return (
    <Grid
      cols={12}
      rowHeight={30}
      margin={[16, 16]}
      layout={rglItems}
      compactType='vertical'
      preventCollision={false}
      isDraggable={editing}
      isResizable={editing}
      draggableHandle=".widget-drag-handle"
      onLayoutChange={handleLayoutChange}
    >
      {rglItems.map(item => (
        <div key={item.i}>{renderCellInner(item)}</div>
      ))}
    </Grid>
  )
}

export default DashboardCanvas
